import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Paper,
  IconButton,
} from "@mui/material";
import {
  CropSquare as SquareIcon,
  CropPortrait as PortraitIcon,
  CropLandscape as LandscapeIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";

const INSTAGRAM_ASPECT_RATIOS = {
  square: { value: 1, label: "Square (1:1)", icon: <SquareIcon /> },
  portrait: { value: 4 / 5, label: "Portrait (4:5)", icon: <PortraitIcon /> },
  landscape: {
    value: 1.91 / 1,
    label: "Landscape (1.91:1)",
    icon: <LandscapeIcon />,
  },
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, fileName) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const croppedFile = new File([blob], fileName, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        resolve(croppedFile);
      },
      "image/jpeg",
      0.95
    );
  });
};

export const InstagramImageCropper = ({
  open,
  onClose,
  image,
  onSave,
  fileName,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState("square");
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleAspectRatioChange = (event, newAspectRatio) => {
    if (newAspectRatio !== null) {
      setAspectRatio(newAspectRatio);
    }
  };

  const handleSaveCropped = async () => {
    if (!croppedAreaPixels || !image) return;

    setIsCropping(true);
    try {
      const croppedImage = await getCroppedImg(
        URL.createObjectURL(image),
        croppedAreaPixels,
        fileName
      );

      // Add metadata to cropped image
      croppedImage.originalName = image.originalName || image.name;
      croppedImage.uniqueName = image.uniqueName;
      croppedImage.isInstagramCropped = true;
      croppedImage.aspectRatio = aspectRatio;

      onSave(croppedImage);

      // IMPORTANT: Do NOT call onClose() here.
      // The parent controls advancing to next image or closing the dialog.
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsCropping(false);
    }
  };

  const handleSkipCropping = () => {
    // Return original image with Instagram skip flag
    const originalImage = new File([image], image.name, {
      type: image.type,
      lastModified: image.lastModified,
    });

    originalImage.originalName = image.originalName || image.name;
    originalImage.uniqueName = image.uniqueName;
    originalImage.isInstagramCropped = false;

    onSave(originalImage);

    // IMPORTANT: Do NOT call onClose() here either.
  };

  if (!image) return null;

  const currentAspectRatio = INSTAGRAM_ASPECT_RATIOS[aspectRatio];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#4B5AB6",
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          📸 Instagram Image Cropper
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
        {/* Aspect Ratio Selection */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e2e8f0" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 2, color: "#1e293b" }}
          >
            Choose Instagram Format:
          </Typography>
          <ToggleButtonGroup
            value={aspectRatio}
            exclusive
            onChange={handleAspectRatioChange}
            sx={{ display: "flex", gap: 1 }}
          >
            {Object.entries(INSTAGRAM_ASPECT_RATIOS).map(([key, ratio]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  flex: 1,
                  flexDirection: "column",
                  gap: 1,
                  py: 2,
                  borderRadius: 2,
                  border: "2px solid #e2e8f0",
                  color: "#64748b",
                  "&.Mui-selected": {
                    bgcolor: "#e0e7ff",
                    color: "#4B5AB6",
                    borderColor: "#4B5AB6",
                  },
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                  },
                }}
              >
                {ratio.icon}
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {ratio.label}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Cropper Area */}
        <Box sx={{ position: "relative", height: 400, bgcolor: "#000" }}>
          <Cropper
            image={URL.createObjectURL(image)}
            crop={crop}
            zoom={zoom}
            aspect={currentAspectRatio.value}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={true}
            style={{
              containerStyle: {
                width: "100%",
                height: "100%",
              },
            }}
          />
        </Box>

        {/* Zoom Control */}
        <Box sx={{ p: 3, borderTop: "1px solid #e2e8f0" }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 2, color: "#1e293b" }}
          >
            Zoom Level:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ZoomOutIcon sx={{ color: "#64748b" }} />
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, zoom) => setZoom(zoom)}
              sx={{
                flex: 1,
                color: "#4B5AB6",
                "& .MuiSlider-thumb": {
                  bgcolor: "#4B5AB6",
                },
                "& .MuiSlider-track": {
                  bgcolor: "#4B5AB6",
                },
              }}
            />
            <ZoomInIcon sx={{ color: "#64748b" }} />
            <Typography
              variant="body2"
              sx={{ minWidth: 60, textAlign: "right", color: "#64748b" }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
          </Box>
        </Box>

        {/* Info Box */}
        <Paper
          sx={{ m: 3, p: 3, bgcolor: "#fffbeb", border: "1px solid #fbbf24" }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#92400e", fontWeight: 600, mb: 1 }}
          >
            📋 Instagram Requirements:
          </Typography>
          <Typography variant="caption" sx={{ color: "#92400e" }}>
            • Square (1:1): Perfect for feed posts
            <br />
            • Portrait (4:5): Maximum engagement format
            <br />• Landscape (1.91:1): Story-friendly dimensions
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: "#f8fafc", gap: 2 }}>
        {/* <Button
          onClick={handleSkipCropping}
          sx={{
            color: "#64748b",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#f1f5f9",
            },
          }}
        >
          Use Original
        </Button> */}

        <Button
          onClick={handleSaveCropped}
          disabled={isCropping}
          variant="contained"
          sx={{
            bgcolor: "#4B5AB6",
            fontWeight: 600,
            px: 4,
            "&:hover": {
              bgcolor: "#3a4891",
            },
          }}
        >
          {isCropping
            ? "Cropping..."
            : `Save for Instagram (${currentAspectRatio.label})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstagramImageCropper;
